import type { PosStore } from './store';
import type { SyncApi } from './types';

/** Nie zu einem Verkauf gewordene Fotos (Zeile wieder entfernt) räumt die Kasse nach so langer Zeit auf. */
export const UNUSED_PHOTO_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** Base64 aus Bytes, in Stücken (ein einziger `String.fromCharCode(...bytes)` bräche bei großen Bildern ab). */
export function toBase64(data: ArrayBuffer): string {
  const bytes = new Uint8Array(data);
  let binary = '';
  const step = 0x8000;
  for (let index = 0; index < bytes.length; index += step) {
    binary += String.fromCharCode(...bytes.subarray(index, index + step));
  }
  return btoa(binary);
}

const statusOf = (error: unknown): number | undefined =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  typeof error.status === 'number'
    ? error.status
    : undefined;

export interface PhotoUploadSummary {
  uploaded: number;
  /** Vom Server endgültig abgelehnt (400, 404, 409): das Foto wird verworfen, der Verkauf bleibt unberührt. */
  dropped: number;
}

/**
 * Lädt Fotos hoch, deren Verkauf der Server schon bestätigt hat (nicht früher: Das Foto ist ohne Verkauf wertlos, und ein
 * abgelehnter Verkauf braucht keines). Läuft im Sync und im Hintergrund-Sender; ein Fehler hält nie einen Verkauf auf.
 * Erfolg (auch „schon bekannt“) löscht das Foto lokal. 400, 404 und 409 sind endgültig (verwerfen), 429 und Netzfehler
 * versuchen es beim nächsten Lauf wieder, ohne die übrigen Fotos zu blockieren (bei Netzfehler endet der Lauf).
 */
export async function uploadPendingPhotos(
  store: PosStore,
  api: SyncApi,
  registerId: string,
): Promise<PhotoUploadSummary> {
  const summary: PhotoUploadSummary = { uploaded: 0, dropped: 0 };
  if (api.uploadPhoto === undefined) return summary;
  await store.purgeUnusedPhotos(UNUSED_PHOTO_MAX_AGE_MS);
  for (const photo of await store.photos()) {
    if (photo.saleClientUuid === undefined) continue;
    const state = await store.saleState(photo.saleClientUuid);
    // Vom Server nicht übernommener Verkauf (Quarantäne): Das Foto bräuchte niemand, es belegte nur Platz auf dem Gerät
    if (state === 'failed') {
      await store.removePhoto(photo.photoId);
      summary.dropped += 1;
      continue;
    }
    if (state !== 'synced') continue;
    try {
      await api.uploadPhoto(registerId, photo.photoId, toBase64(photo.data));
      await store.removePhoto(photo.photoId);
      summary.uploaded += 1;
    } catch (error) {
      const status = statusOf(error);
      if (status === 400 || status === 404 || status === 409) {
        await store.removePhoto(photo.photoId);
        summary.dropped += 1;
        continue;
      }
      // 429 (Tageslimit) und alles Übrige: später wieder; bei Netzfehler hat es keinen Sinn, weiterzumachen
      if (status === 429) continue;
      return summary;
    }
  }
  return summary;
}
